/**
 * Что происходит по нажатию на уведомление.
 *
 * Напоминание застаёт человека в дороге, за рулём, с ребёнком на руках — и
 * до этого модуля отвечать на него было нечем: нажатие открывало приложение
 * где придётся, а кнопок не было вовсе. Стрелка в шторке раскрывала текст и
 * больше ничего не давала.
 *
 * Теперь исходов два, и оба короткие. Нажатие по самому уведомлению открывает
 * тот раздел, о котором напомнили. Кнопка делает дело сразу: «Принял»
 * отмечает приём, «Отложить» переносит его на четверть часа, «Выполнено»
 * закрывает день привычки.
 *
 * Открывать приложение ради кнопки не нужно — но Android всё равно выносит его
 * на экран, и человек оказывался там, где закрыл его в прошлый раз: отметил
 * приём лекарства, а перед глазами привычки. Поэтому, если приложение
 * поднимают этим нажатием, оно открывается на том разделе, о котором
 * напомнили. Если же оно и так было открыто, экран не меняется: выдёргивать
 * человека из того, чем он занят, кнопка права не имеет.
 */

import { doseEventsRepo } from '@/entities/dose/model/repository';
import { habitsRepo } from '@/entities/habit/model/repository';
import { getEntriesInRange, toggleHabitDone } from '@/entities/habit/model/habit-service';
import { medicationsRepo } from '@/entities/medication/model/repository';
import { HABIT_VALUE } from '@/shared/db/schema';
import { record } from '@/shared/lib/diagnostics';
import { toast } from '@/shared/ui';
import type { NotificationAction } from '@/shared/notifications/local-notifications';

import { snoozeDose, takeDose } from '@/features/medication-today/model/dose-actions';

/** Приём: отметить принятым или отложить. */
async function applyToDose(actionId: string, doseId: string): Promise<void> {
  const dose = await doseEventsRepo.get(doseId);
  if (!dose || dose.deletedAt !== null) return;

  if (actionId === 'snooze') {
    await snoozeDose(dose);
    return;
  }

  const medication = await medicationsRepo.get(dose.medicationId);
  await takeDose(dose, medication ?? undefined);
}

/**
 * Привычка: закрыть день.
 *
 * Отметка ставится ровно так же, как нажатием в приложении, — одной и той же
 * функцией: иначе «Выполнено» из уведомления и «Выполнено» на экране
 * разошлись бы в подсчёте серий.
 */
async function applyToHabit(habitId: string, date: string): Promise<void> {
  const habit = await habitsRepo.get(habitId);
  if (!habit || habit.deletedAt !== null) return;

  const entries = await getEntriesInRange(date, date);
  const current = entries.find((entry) => entry.habitId === habitId)?.value ?? HABIT_VALUE.UNKNOWN;

  await toggleHabitDone(habit, date, current);
}

/**
 * Выполняет то, что человек выбрал в уведомлении.
 *
 * `navigate` вызывается только для нажатия по самому уведомлению: кнопка
 * отвечает на месте, и открывать ради неё экран — значит отнимать то самое
 * время, ради экономии которого кнопка и нужна.
 */
export async function handleNotificationAction(
  action: NotificationAction,
  navigate: (route: string) => void,
): Promise<void> {
  try {
    const { actionId, target } = action;

    // Приложение поднимают этим нажатием — значит, показать надо то, о чём
    // напомнили, а не экран, на котором его закрыли в прошлый раз.
    const opening = document.visibilityState !== 'visible';

    if (actionId === 'tap' || !target) {
      navigate(action.route ?? '/');
      return;
    }

    if (target.kind === 'dose') {
      await applyToDose(actionId, target.id);
      toast.success(actionId === 'snooze' ? 'Приём отложен на 15 минут' : 'Приём отмечен');
    }

    if (target.kind === 'habit' && actionId === 'done') {
      await applyToHabit(target.id, target.date);
      toast.success('Привычка отмечена');
    }

    if (opening) navigate(action.route ?? '/');
  } catch (error) {
    /*
      Нажатие по уведомлению — не то место, где можно показать ошибку: экрана
      может не быть вовсе. Но и терять её нельзя: «нажала „Принял“, а приём не
      отметился» разбирается только по журналу.
    */
    record('error', 'уведомление.действие', error);
  }
}
